import { useState } from "react";
import dayjs from "dayjs";
import { CalendarIcon } from "lucide-react";
import CustomDatePicker from "../../CustomDatePicker";
import { Button, MagicToast, Text } from "../../../DesignSystem";
import DateUtil from "../../../../util/DateUtil";

/** What to tell the viewer when Apply is pressed without enough dates, or null when there are enough. */
function missingDatesMessage(props: { startDate: number | null; endDate: number | null; openEnded: boolean }): string | null {
    if (props.startDate === null && props.endDate === null) {
        return props.openEnded
            ? "Choose a start date, an end date, or both to apply this filter."
            : "Choose a start and end date to apply this filter.";
    }
    if (props.openEnded) {
        return null;
    }
    if (props.startDate === null) {
        return "Choose a start date to apply this filter.";
    }
    if (props.endDate === null) {
        return "Choose an end date to apply this filter.";
    }
    return null;
}

/** The start/end pair behind a menu's "Custom Timeframe..." option. label names the field. */
export const CustomDateRangeForm = (props: {
    /** The field being filtered, e.g. "Created Date" or "Date Sent". */
    label: string;
    /** Allows leaving the start or the end empty, for "since" or "until" a date. Otherwise both are required. */
    openEnded?: boolean;
    /** The range already applied, so reopening the form shows it. */
    initialRange?: { start?: number; end?: number };
    /** Allows picking days after today, for fields that can hold future dates. */
    allowFutureDates?: boolean;
    /** The end, when chosen, runs through the whole of its day. */
    didChooseDates: (range: { start?: number; end?: number }) => void;
    didChooseCancel: () => void;
}) => {
    const [startDate, setStartDate] = useState<number | null>(props.initialRange?.start ?? null);
    // Shown as its day. Apply runs it through the end of that day again.
    const [endDate, setEndDate] = useState<number | null>(props.initialRange?.end !== undefined ? dayjs(props.initialRange.end).startOf("day").valueOf() : null);

    // The latest day either picker allows, or none when future dates are allowed.
    const latestDay = props.allowFutureDates ? undefined : DateUtil.startOfCurrent("day");

    return (
        <div className="flex h-full w-full flex-col p-2">
            <Text variant="body500" className="pb-3">
                {props.label} <span className="text-stone-500">in</span>
            </Text>

            {/* Headings get their own row so the dash centers on the inputs alone. */}
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-x-3 gap-y-1 pb-6">
                <Text variant="smallBody600">Start Date</Text>
                <div />
                <Text variant="smallBody600">End Date</Text>
                <DatePickerWrapper value={startDate} setValue={setStartDate} maxDate={endDate ?? latestDay} />
                <Text variant="body500">-</Text>
                <DatePickerWrapper value={endDate} setValue={setEndDate} minDate={startDate} maxDate={latestDay} />
            </div>

            <div className="flex w-full grow items-center justify-end gap-3">
                <Button color="clear" text="Cancel" onClick={props.didChooseCancel} />
                <Button
                    color="bronze"
                    text="Apply"
                    onClick={() => {
                        const missingMessage = missingDatesMessage({ startDate, endDate, openEnded: props.openEnded === true });
                        if (missingMessage !== null) {
                            MagicToast.show(missingMessage);
                            return;
                        }
                        if (startDate !== null && endDate !== null && endDate < startDate) {
                            MagicToast.show("Choose an end date on or after the start date.");
                            return;
                        }
                        // The range runs through the whole of its end day, not midnight at its start.
                        props.didChooseDates({
                            start: startDate ?? undefined,
                            end: endDate !== null ? DateUtil.endOfDay(endDate) : undefined,
                        });
                    }}
                />
            </div>
        </div>
    );
};

function DatePickerWrapper(props: { value: number | null; setValue: (next: number) => void; minDate?: number | null; maxDate?: number }) {
    const { value, setValue, minDate, maxDate } = props;

    return (
        <CustomDatePicker
            view={
                <div className="flex items-center gap-1 rounded border border-stone-200 px-2 py-1 hover:bg-stone-100">
                    <CalendarIcon className="h-4 w-4 text-stone-400" />
                    <Text className={value === null ? "text-stone-400" : undefined}>
                        {value === null ? "MM / DD / YYYY" : DateUtil.formatted(value, { format: "MM / DD / YYYY" })}
                    </Text>
                </div>
            }
            selectedDate={value}
            setSelectedDate={setValue}
            minDate={minDate}
            maxDate={maxDate}
        />
    );
}
